import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Link, Skeleton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { throttle } from "lodash";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute, useSectionCalendars, RAIL_PAGE_SIZE } from "src/services";
import { trackRailViewed, trackSeeAllClicked } from "src/utils/sectionAnalytics.js";
import { clampOverscroll, preserveOffsetOnSlidesChanged, railArrowSx, scrolledLead } from "src/utils/emblaUtils.js";
import { prefetchAllowed, whenIdle } from "src/utils/schedulePrefetch.js";
import { CONTENT_GUTTER_BLEED } from "src/styles/layout.js";
import { useDimensions } from "src/utils/useDimensions.js";
import { useInViewOnce } from "src/utils/useInViewOnce.js";
import { warmCalendarImages } from "src/utils/warmCalendarImages.js";
import CalendarTile from "./CalendarTile.js";
import { isTvCalendar } from "./calendarTileUtils.js";

const RAIL_OPTIONS = {
  align: "start",
  dragFree: true,
  containScroll: "trimSnaps",
  slidesToScroll: 3,
  watchSlides: preserveOffsetOnSlidesChanged,
};

/**
 * Fixed landscape slide: a constant width tied to THUMB_HEIGHT for a steady
 * ratio, so tiles don't grow unbounded or drift in ratio on large screens —
 * more just fit per row. Breakpoint matches CalendarTile's compact/wide.
 */
const SLIDE_SX = {
  flex: "0 0 auto",
  minWidth: 0,
  width: { xs: "230px", md: "285px" },
};

/**
 * Fixed poster slide (TV/movie rails): a constant width tied to POSTER_THUMB_HEIGHT
 * for a steady 2:3, so posters don't grow unbounded or drift in ratio on large
 * screens — more just fit per row. Breakpoint matches CalendarTile's compact/wide.
 */
const POSTER_SLIDE_SX = {
  flex: "0 0 auto",
  minWidth: 0,
  width: { xs: "150px", md: "190px" },
};

/** Section categories whose calendars are portrait poster art (2:3). */
const POSTER_CATEGORIES = new Set(["tv", "movies"]);

/**
 * Slides whose art resolves before the rail can be measured — the first paint's
 * floor, superseded by the measured window as soon as Embla attaches.
 */
const EAGER_SLIDES = { compact: 5, wide: 9 };

/** Posters are narrower, so more of them fit a wide first screen. */
const POSTER_EAGER_SLIDES = { compact: 5, wide: 12 };

/** Slides past the last visible one whose art resolves ahead of the drag. */
const ART_LOOKAHEAD = { compact: 4, wide: 8 };

/**
 * The art window only grows, and only in whole groups, so a continuous drag
 * re-renders the rail once every few slides instead of once a frame.
 */
const ART_WINDOW_STEP = 3;

/**
 * Extra pages a rail may pull on its own to keep a deeper horizontal buffer ready.
 * Past this the rail is the user's to extend by dragging.
 */
const AUTOFILL_MAX_PAGES = 3;

/** Ready screens of content a nearby rail tries to keep before interaction. */
const AUTOFILL_READY_SCREENS = { compact: 3, wide: 3.5 };

/** A rail a few pixels short of its ready buffer is full. */
const AUTOFILL_SLACK_PX = 24;

/** Spacing between the auto-fill requests of rails that fill on the same paint. */
const AUTOFILL_SPACING_MS = 200;

/** Timeout for low-priority horizontal warmup work. */
const RAIL_WARMUP_IDLE_TIMEOUT_MS = 1800;

/** Screens of track left before the next page is pulled ahead of the drag. */
const RAIL_PREFETCH_LEAD_SCREENS = 3;

/** Vertical lead before a rail starts resolving its first slides' art. */
const RAIL_ROOT_MARGIN = "400px";

/**
 * Bleeds the row past the shell's right gutter, so tiles run off the viewport
 * edge instead of stopping short of it. The heading above keeps the gutter.
 */
const RAIL_SCROLLER_SX = { position: "relative", mr: CONTENT_GUTTER_BLEED };

/** Sized to an `IconButton`, so the spinner sits exactly where the arrow was. */
const RAIL_SPINNER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  borderRadius: "50%",
};

/**
 * Next free slot on a clock shared by every rail on the page, so a feed whose
 * rails all fill at once spaces its requests instead of bursting them.
 */
let autofillNextSlot = 0;

/**
 * Claims the next auto-fill slot.
 *
 * @returns {number} Milliseconds to wait before firing.
 */
const claimAutofillSlot = () => {
  const now = Date.now();
  const at = Math.max(now, autofillNextSlot);
  autofillNextSlot = at + AUTOFILL_SPACING_MS;
  return at - now;
};

/**
 * One slide, memoized apart from the row so growing the art window re-renders
 * the tiles it just took in rather than every tile on the rail.
 */
const RailTileSkeleton = ({ poster }) => (
  <Box data-testid="deferred-rail-tile" aria-hidden="true">
    <Skeleton
      animation="wave"
      variant="rectangular"
      width="100%"
      sx={{
        height: poster ? { xs: 225, md: 285 } : { xs: 150, md: 185 },
        borderRadius: "12px",
      }}
    />
    <Skeleton animation="wave" width="78%" height={24} sx={{ mt: 1 }} />
    <Skeleton animation="wave" width="52%" height={24} />
    <Skeleton animation="wave" variant="rectangular" width="100%" height={36} sx={{ mt: 1, borderRadius: 1 }} />
  </Box>
);

/**
 * Placeholder for a whole section rail before the home feed has received its
 * first page. It shares the exact slide geometry with the deferred tiles used
 * by a loaded rail, so the transition to real calendars is visually stable.
 */
export const SectionRailSkeleton = ({ poster = false }) => (
  <Box component="section" data-testid="section-rail-skeleton" aria-hidden sx={{ mb: 7 }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 1.5 }}>
      <Skeleton animation="wave" variant="text" width="min(42%, 260px)" height={32} />
      <Skeleton animation="wave" variant="text" width={56} height={20} sx={{ ml: "auto" }} />
    </Box>
    <Box sx={RAIL_SCROLLER_SX}>
      <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
        {Array.from({ length: 5 }, (_, index) => (
          <Box key={index} sx={poster ? POSTER_SLIDE_SX : SLIDE_SX}>
            <RailTileSkeleton poster={poster} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const RailSlide = memo(({ calendar, section, poster, preloadArt, hydrate }) => (
  <Box sx={poster ? POSTER_SLIDE_SX : SLIDE_SX}>
    {hydrate ? (
      <CalendarTile
        calendar={calendar}
        section={section}
        poster={poster}
        preloadArt={preloadArt}
      />
    ) : (
      <RailTileSkeleton poster={poster} />
    )}
  </Box>
));
RailSlide.displayName = "RailSlide";

/**
 * One horizontally-scrolling rail. Opens with the calendars the feed already
 * returned, fills itself to the viewport's width, and pulls further pages from
 * the per-section endpoint ahead of the drag.
 *
 * @param {{ section: object }} props
 */
const SectionRail = ({ section }) => {
  const { user } = useContext(AuthContext);
  const { isMediumScreen } = useDimensions();
  const viewedRef = useRef(false);
  // Set on the first drag/arrow; autonomous warmup stays gated separately.
  const interactedRef = useRef(false);
  const [paginationStarted, setPaginationStarted] = useState(false);
  const [railRef, railInView] = useInViewOnce({ threshold: 0.4 });
  const [scrollerRef, railNearView] = useInViewOnce({ rootMargin: RAIL_ROOT_MARGIN });

  // A plugin instance keeps its teardown in a closure it overwrites on every
  // `init`, so sharing one across rails leaks every rail but the last.
  const plugins = useMemo(() => [WheelGesturesPlugin({ forceWheelAxis: "x" })], []);
  const [emblaRef, emblaApi] = useEmblaCarousel(RAIL_OPTIONS, plugins);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Geometry, measured on init/reInit/resize rather than per frame: reading
  // offsets inside a scroll handler forces a reflow after Embla has already
  // written the frame's transform.
  const slideOffsetsRef = useRef([]);
  const trackWidthRef = useRef(0);
  const viewportWidthRef = useRef(0);
  const [geometryTick, setGeometryTick] = useState(0);

  const [artWindow, setArtWindow] = useState(0);
  const artWindowRef = useRef(0);

  // Pages this rail has bought itself, the page in flight, the tile count when
  // it was asked for, and whether that page has been seen in flight yet.
  const autofillPagesRef = useRef(0);
  const autofillPendingRef = useRef(false);
  const autofillFromRef = useRef(0);
  const autofillSawFetchRef = useRef(false);

  // Personalized rails are user-scoped and have no per-slug endpoint, so they
  // render exactly what the feed returned.
  const seedCount = section.calendars?.length ?? 0;
  // Posters for TV/movie-categorized sections, and for any non-sports section
  // that seeds a TV title (sports stays landscape). Based on the seed slice, not
  // paginated pages, so the format stays stable as more load.
  const isPoster =
    POSTER_CATEGORIES.has(section.category) ||
    (section.category !== "sports" &&
      (section.calendars ?? []).some(isTvCalendar));
  const isPageable = section.source !== "personalized" && seedCount > 0;
  // Pages are sized to the seed plus a full page: a section whose stored
  // snapshot is tiny would otherwise paginate in steps of that snapshot. The
  // merge below drops the rows the seed already holds.
  const {
    calendars: extraCalendars,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useSectionCalendars(section.slug, {
    pageSize: seedCount + RAIL_PAGE_SIZE,
    initialPage: 1,
    enabled: isPageable && paginationStarted,
  });

  const calendars = useMemo(() => {
    const seen = new Set();
    return [...(section.calendars ?? []), ...extraCalendars].filter((calendar) => {
      const id = String(calendar.calendarId);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [section.calendars, extraCalendars]);

  // One page per call. Enabling the query fetches the first extra page; every
  // later page is an explicit fetchNextPage.
  const loadMore = useCallback(() => {
    if (!isPageable) return;
    if (!paginationStarted) {
      setPaginationStarted(true);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [isPageable, paginationStarted, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // The `reInit` that appending slides forces arrives from a MutationObserver,
  // before React has flushed the effects that would re-subscribe with fresh
  // state. A latest-ref keeps one subscription for the carousel's life.
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const measureRail = useCallback(() => {
    if (!emblaApi) return;
    const nodes = emblaApi.containerNode().children;
    if (!nodes.length) {
      slideOffsetsRef.current = [];
      trackWidthRef.current = 0;
      return;
    }
    const base = nodes[0].offsetLeft;
    slideOffsetsRef.current = Array.from(nodes, (node) => node.offsetLeft - base);
    const last = nodes[nodes.length - 1];
    trackWidthRef.current = last.offsetLeft + last.offsetWidth - base;
    viewportWidthRef.current = emblaApi.rootNode().clientWidth;
  }, [emblaApi]);

  const syncArtWindow = useCallback(
    (lookahead) => {
      const offsets = slideOffsetsRef.current;
      if (!offsets.length || !emblaApi) return;
      const edge = scrolledLead(emblaApi) + viewportWidthRef.current;
      let last = 0;
      while (last + 1 < offsets.length && offsets[last + 1] < edge) last += 1;
      const wanted =
        Math.ceil((last + 1 + lookahead) / ART_WINDOW_STEP) * ART_WINDOW_STEP;
      // Never shrinks: dropping a resolved tile would bring the pop-in back on
      // the way past it again.
      if (wanted <= artWindowRef.current) return;
      artWindowRef.current = wanted;
      setArtWindow(wanted);
    },
    [emblaApi],
  );

  const lookahead = isMediumScreen ? ART_LOOKAHEAD.compact : ART_LOOKAHEAD.wide;
  const readyScreens = isMediumScreen
    ? AUTOFILL_READY_SCREENS.compact
    : AUTOFILL_READY_SCREENS.wide;

  useEffect(() => {
    if (!emblaApi) return undefined;

    const update = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      // Dragged to the end — pull the next page.
      if (!emblaApi.canScrollNext() && interactedRef.current) loadMoreRef.current();
    };
    const markInteracted = () => {
      interactedRef.current = true;
    };
    // Art mounts ahead of the drag as before, but at most once per interval:
    // unthrottled, every momentum frame that crossed a step boundary committed
    // a tile batch mid-gesture, which is what made these rails stutter.
    const throttledSyncArtWindow = throttle(
      () => syncArtWindow(lookahead),
      90,
      { leading: true, trailing: true },
    );

    const onScroll = () => {
      clampOverscroll(emblaApi);
      throttledSyncArtWindow();
      // Approaching the end — start the next page before the drag lands on it.
      if (!interactedRef.current || !prefetchAllowed()) return;
      const viewport = viewportWidthRef.current;
      const remaining =
        trackWidthRef.current - (scrolledLead(emblaApi) + viewport);
      if (remaining <= viewport * RAIL_PREFETCH_LEAD_SCREENS) loadMoreRef.current();
    };
    // A reInit from an appended page must not refresh the auto-fill budget; a
    // viewport that actually got wider must.
    const remeasure = () => {
      measureRail();
      syncArtWindow(lookahead);
      setGeometryTick((tick) => tick + 1);
    };
    const onResize = () => {
      autofillPagesRef.current = 0;
      remeasure();
    };

    // The settle pass flushes whatever the throttle deferred at gesture end.
    const onSettle = () => {
      update();
      syncArtWindow(lookahead);
    };

    emblaApi.on("pointerDown", markInteracted);
    emblaApi.on("scroll", onScroll);
    emblaApi.on("select", update);
    emblaApi.on("settle", onSettle);
    emblaApi.on("reInit", update);
    emblaApi.on("reInit", remeasure);
    emblaApi.on("resize", onResize);
    update();
    remeasure();

    return () => {
      emblaApi.off("pointerDown", markInteracted);
      emblaApi.off("scroll", onScroll);
      emblaApi.off("select", update);
      emblaApi.off("settle", onSettle);
      emblaApi.off("reInit", update);
      emblaApi.off("reInit", remeasure);
      emblaApi.off("resize", onResize);
      throttledSyncArtWindow.cancel();
    };
  }, [emblaApi, measureRail, syncArtWindow, lookahead]);

  // Releases the in-flight guard. A page that lands without adding a tile —
  // the feed's seed and the paginated snapshot overlap, and the dedupe drops
  // the overlap — would leave the deficit unchanged, so it ends auto-fill
  // rather than buying another page that lands the same way.
  useEffect(() => {
    if (!autofillPendingRef.current) return;
    if (calendars.length > autofillFromRef.current) {
      autofillPendingRef.current = false;
      autofillSawFetchRef.current = false;
      return;
    }
    if (isFetching || isFetchingNextPage) {
      autofillSawFetchRef.current = true;
      return;
    }
    // Only conclusive once the request has been seen in flight — before that,
    // an idle query just means the staggered timer has not fired yet.
    if (autofillSawFetchRef.current) {
      autofillPagesRef.current = AUTOFILL_MAX_PAGES;
      autofillPendingRef.current = false;
      autofillSawFetchRef.current = false;
    }
  }, [calendars.length, isFetching, isFetchingNextPage]);

  useEffect(() => {
    if (!emblaApi || !isPageable) return undefined;
    // Only rails near the viewport warm themselves, so a feed of rails does not
    // open every section lookup on one paint. Connection gating keeps this
    // speculative work off save-data / very slow networks.
    if (!railNearView || !prefetchAllowed()) return undefined;
    if (autofillPendingRef.current) return undefined;
    if (autofillPagesRef.current >= AUTOFILL_MAX_PAGES) return undefined;
    if (paginationStarted && (!hasNextPage || isFetching || isFetchingNextPage)) {
      return undefined;
    }
    measureRail();
    const targetTrackWidth = viewportWidthRef.current * readyScreens;
    const needsReadyBuffer =
      targetTrackWidth - trackWidthRef.current > AUTOFILL_SLACK_PX;
    const canDeepPrefetch = paginationStarted && Boolean(hasNextPage);
    if (!needsReadyBuffer && !canDeepPrefetch) {
      return undefined;
    }

    // Claimed before scheduling, so a re-render inside the delay cannot claim a
    // second slot for the same page. If no request is in flight, nearby rails keep
    // restocking proactively up to AUTOFILL_MAX_PAGES instead of waiting for
    // hover/focus or a hard edge. The actual fetch is still deferred to idle
    // time to avoid competing with initial paint / scroll frames.
    autofillPagesRef.current += 1;
    autofillPendingRef.current = true;
    autofillSawFetchRef.current = false;
    autofillFromRef.current = calendars.length;
    let fired = false;
    let cancelIdle;
    const timer = setTimeout(() => {
      cancelIdle = whenIdle(
        () => {
          fired = true;
          loadMoreRef.current();
        },
        { timeout: RAIL_WARMUP_IDLE_TIMEOUT_MS },
      );
    }, claimAutofillSlot());
    return () => {
      clearTimeout(timer);
      if (cancelIdle) cancelIdle();
      // Cancelled before it asked for anything — hand the page back, or the
      // rail would sit on a claim it never spent.
      if (!fired) {
        autofillPendingRef.current = false;
        autofillPagesRef.current -= 1;
      }
    };
  }, [
    emblaApi,
    isPageable,
    railNearView,
    readyScreens,
    paginationStarted,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    calendars.length,
    geometryTick,
    measureRail,
  ]);

  useEffect(() => {
    if (!railInView || viewedRef.current) return;
    viewedRef.current = true;
    trackRailViewed({ user, section });
  }, [railInView, user, section]);

  const eagerSlides = isPoster ? POSTER_EAGER_SLIDES : EAGER_SLIDES;
  const eagerCount = isMediumScreen ? eagerSlides.compact : eagerSlides.wide;
  const artCount = Math.max(eagerCount, artWindow);

  // Warm the actual image requests independently of React's component work.
  // The window is intentionally capped to visible art plus look-ahead, rather
  // than every calendar in the speculative data buffer.
  useEffect(() => {
    if (!railNearView || !prefetchAllowed()) return;
    warmCalendarImages(calendars.slice(0, artCount));
  }, [railNearView, calendars, artCount]);

  const slides = useMemo(
    () =>
      calendars.map((calendar, index) => (
        <RailSlide
          key={calendar.calendarId}
          calendar={calendar}
          section={section}
          poster={isPoster}
          hydrate={railNearView}
          preloadArt={railNearView && index < artCount}
        />
      )),
    [calendars, section, isPoster, railNearView, artCount],
  );

  if (!calendars.length) return null;

  const seeAllTo = getPageRoute("home", "SECTION_SEE_ALL", { slug: section.slug });
  // A rail whose tiles happen to fit the viewport still has more to fetch, so
  // the arrow stays available and pulls the next page instead of scrolling.
  const hasMoreToFetch = isPageable && (!paginationStarted || Boolean(hasNextPage));

  const handlePrefetchIntent = () => {
    if (!prefetchAllowed()) return;
    loadMore();
  };

  const handleScrollNext = () => {
    interactedRef.current = true;
    if (emblaApi?.canScrollNext()) emblaApi.scrollNext();
    else loadMore();
  };

  return (
    <Box ref={railRef} component="section" data-testid="section-rail" sx={{ mb: 7 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="sectionTitle" component="h2">
            {section.title}
          </Typography>
          {section.description && (
            <Typography variant="body2" sx={{ fontSize: "13px", color: "text.quaternary" }}>
              {section.description}
            </Typography>
          )}
        </Box>
        {isPageable && (
          <Link
            component={RouterLink}
            to={seeAllTo}
            data-testid="section-see-all"
            onClick={() => trackSeeAllClicked({ user, section })}
            sx={{ ml: "auto", flexShrink: 0, fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            See all →
          </Link>
        )}
      </Box>

      <Box ref={scrollerRef} sx={RAIL_SCROLLER_SX}>
        {/* Dragging the rail would otherwise highlight the tile captions. */}
        <Box ref={emblaRef} sx={{ overflow: "hidden", userSelect: "none" }}>
          {/* Embla reInits on any child added here and restores the nearest
              snap, so nothing but slides may live in the container. */}
          <Box sx={{ display: "flex", gap: 2 }}>{slides}</Box>
        </Box>

        {!isMediumScreen && canScrollPrev && (
          <IconButton
            onClick={() => emblaApi?.scrollPrev()}
            aria-label={`Scroll ${section.title} left`}
            sx={railArrowSx("left")}
          >
            <ChevronLeft />
          </IconButton>
        )}
        {!isMediumScreen && (canScrollNext || (hasMoreToFetch && !isFetchingNextPage)) && (
          <IconButton
            onClick={handleScrollNext}
            onFocus={handlePrefetchIntent}
            onMouseEnter={handlePrefetchIntent}
            aria-label={`Scroll ${section.title} right`}
            data-testid="section-rail-next"
            sx={railArrowSx("right")}
          >
            <ChevronRight />
          </IconButton>
        )}
        {/* Prefetch must never block horizontal movement: keep the arrow while
            existing slides can still move, and only show progress at the hard end. */}
        {!isMediumScreen && isFetchingNextPage && !canScrollNext && (
          <Box sx={{ ...railArrowSx("right"), ...RAIL_SPINNER_SX }}>
            <CircularProgress thickness={2} size={24} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// `memo`: the feed renders one rail per section, so a change in one rail's
// neighbourhood (sibling pagination, shell state) must not re-render them all.
export default memo(SectionRail);
